import React from 'react';
import { RefreshCw, Check, X, CloudOff } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { useOfflineSync } from '@shared/hooks/useOfflineSync';
import { cn } from '@shared/lib/utils';

// Shared body of the Sync History page: a header row (status + Sync now) and a
// color-coded list of past sync runs. Each app wraps this with its own nav.
export default function SyncHistoryList() {
  const {
    isOnline,
    isSyncing,
    pendingOperations,
    syncHistory,
    syncNow,
    clearSyncHistory,
  } = useOfflineSync();

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 md:px-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-semibold">Sync history</h1>
          <p className="text-sm text-muted-foreground">
            {pendingOperations > 0
              ? `${pendingOperations} change(s) waiting to sync`
              : 'All changes synced'}
          </p>
        </div>
        <Button onClick={syncNow} disabled={isSyncing || !isOnline} size="sm">
          <RefreshCw className={cn('mr-2 h-4 w-4', isSyncing && 'animate-spin')} />
          {isSyncing ? 'Syncing…' : 'Sync now'}
        </Button>
      </div>

      {syncHistory.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <CloudOff className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No syncs yet.</p>
          <p className="text-xs text-muted-foreground">
            Your changes are saved locally until you sync.
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {syncHistory.map((entry, idx) => (
              <li key={`${entry.time}-${idx}`} className="flex items-center gap-3 px-4 py-3">
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    entry.ok ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  )}
                >
                  {entry.ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">
                    {entry.ok ? 'Synced' : 'Sync incomplete'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {entry.pushed} pushed
                    {entry.failed > 0 && ` · ${entry.failed} failed`}
                    {` · ${entry.pulled ? 'pulled latest' : 'pull failed'}`}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-muted-foreground">
                  {formatTime(entry.time)}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={clearSyncHistory}>
              Clear history
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
